import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  detectUserLocation, 
  getExchangeRates, 
  formatPrice,
  getCurrencyForCountry,
  ALL_CURRENCIES,
  GATEWAY_CURRENCIES
} from '@/utils/currencyDetection';

const CurrencyContext = createContext();

// Default currency is always USD
const DEFAULT_CURRENCY = 'USD';

export const CurrencyProvider = ({ children }) => {
  const [detectedLocation, setDetectedLocation] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null); // Manual override
  const [exchangeRates, setExchangeRates] = useState({ USD: 1 });
  const [loading, setLoading] = useState(true);

  // Refresh location detection every 10 seconds
  const refreshLocation = useCallback(async () => {
    try {
      const location = await detectUserLocation(true); // Force refresh
      setDetectedLocation(location);
      // Only auto-set currency if user hasn't manually selected one
      if (!selectedCurrency) {
        // Don't auto-set, let them choose or use detected
      }
    } catch (error) {
      console.error('Location detection failed:', error);
    }
  }, [selectedCurrency]);

  useEffect(() => {
    const init = async () => {
      try {
        const [location, rates] = await Promise.all([
          detectUserLocation(),
          getExchangeRates('USD')
        ]);
        setDetectedLocation(location);
        setExchangeRates(rates);
      } catch (error) {
        setDetectedLocation({
          countryCode: 'US',
          country: 'United States',
          currency: { code: 'USD', symbol: '$', name: 'US Dollar' }
        });
      } finally {
        setLoading(false);
      }
    };
    init();

    // Refresh location every 10 seconds
    const interval = setInterval(refreshLocation, 10000);
    return () => clearInterval(interval);
  }, [refreshLocation]);

  // Get the active currency (manual selection > detected > USD)
  const getActiveCurrency = () => {
    if (selectedCurrency) return selectedCurrency;
    if (detectedLocation?.currency?.code) return detectedLocation.currency.code;
    return DEFAULT_CURRENCY;
  };

  // Manually set preferred currency
  const setCurrency = (currencyCode) => {
    setSelectedCurrency(currencyCode);
    localStorage.setItem('preferredCurrency', currencyCode);
  };

  // Clear manual selection (use auto-detect)
  const clearCurrencySelection = () => {
    setSelectedCurrency(null);
    localStorage.removeItem('preferredCurrency');
  };

  // Load saved preference on mount
  useEffect(() => {
    const saved = localStorage.getItem('preferredCurrency');
    if (saved && ALL_CURRENCIES[saved]) {
      setSelectedCurrency(saved);
    }
  }, []);

  // Convert USD to any currency
  const convertTo = (usdAmount, currencyCode) => {
    if (currencyCode === 'USD') return usdAmount;
    const rate = exchangeRates[currencyCode] || 1;
    return Math.round(usdAmount * rate * 100) / 100;
  };

  // Convert USD to user's active currency
  const convertToActive = (usdAmount) => {
    return convertTo(usdAmount, getActiveCurrency());
  };

  // Format price in specific currency
  const formatInCurrency = (amount, currencyCode) => {
    const currency = ALL_CURRENCIES[currencyCode];
    if (!currency) return `$${amount.toFixed(2)}`;
    
    const formatted = amount.toLocaleString(undefined, {
      minimumFractionDigits: amount < 100 ? 2 : 0,
      maximumFractionDigits: amount < 100 ? 2 : 0
    });
    return `${currency.symbol}${formatted}`;
  };

  // Format dual price (USD + active currency)
  const formatDualPrice = (usdAmount) => {
    const usdFormatted = `$${usdAmount.toFixed(2)}`;
    const activeCurrency = getActiveCurrency();
    
    if (loading || activeCurrency === 'USD') {
      return { usd: usdFormatted, local: null, localCode: 'USD' };
    }
    
    const rate = exchangeRates[activeCurrency];
    if (!rate) {
      return { usd: usdFormatted, local: null, localCode: activeCurrency };
    }
    
    const localAmount = usdAmount * rate;
    const localFormatted = formatInCurrency(localAmount, activeCurrency);
    
    return {
      usd: usdFormatted,
      local: localFormatted,
      localCode: activeCurrency
    };
  };

  // Format price in user's LOCAL currency ONLY
  const formatLocalPrice = (usdAmount) => {
    const activeCurrency = getActiveCurrency();
    
    if (loading) {
      return '...';
    }
    
    // If USD, just show USD
    if (activeCurrency === 'USD') {
      return `$${usdAmount.toFixed(2)}`;
    }
    
    const rate = exchangeRates[activeCurrency];
    if (!rate) {
      // Fallback to USD if no rate
      return `$${usdAmount.toFixed(2)}`;
    }
    
    const localAmount = usdAmount * rate;
    return formatInCurrency(localAmount, activeCurrency);
  };

  // Check if gateway supports a currency
  const gatewaySupports = (gatewaySlug, currencyCode) => {
    const supported = GATEWAY_CURRENCIES[gatewaySlug];
    if (!supported) return false;
    return supported.includes(currencyCode);
  };

  // Get payment currency for gateway (customer currency if supported, else USD)
  const getPaymentCurrency = (gatewaySlug, billingCountryCode) => {
    const billingCurrency = getCurrencyForCountry(billingCountryCode);
    const customerCurrency = billingCurrency?.code || getActiveCurrency();
    
    // If gateway supports customer's currency, use it
    if (gatewaySupports(gatewaySlug, customerCurrency)) {
      return customerCurrency;
    }
    
    // Otherwise, use USD (default)
    if (gatewaySupports(gatewaySlug, DEFAULT_CURRENCY)) {
      return DEFAULT_CURRENCY;
    }
    
    // Fallback: first supported currency (shouldn't reach here normally)
    const supported = GATEWAY_CURRENCIES[gatewaySlug];
    return supported?.[0] || DEFAULT_CURRENCY;
  };

  // Check if gateway should be shown (supports customer currency OR USD)
  const shouldShowGateway = (gatewaySlug, billingCountryCode) => {
    const billingCurrency = getCurrencyForCountry(billingCountryCode);
    const customerCurrency = billingCurrency?.code || getActiveCurrency();
    
    // Show if gateway supports customer's currency OR USD
    return gatewaySupports(gatewaySlug, customerCurrency) || 
           gatewaySupports(gatewaySlug, DEFAULT_CURRENCY);
  };

  // Get payment amount in the correct currency for gateway
  const getPaymentAmount = (usdAmount, gatewaySlug, billingCountryCode) => {
    const currency = getPaymentCurrency(gatewaySlug, billingCountryCode);
    const amount = convertTo(usdAmount, currency);
    return { amount, currency };
  };

  // Get checkout summary prices (only show relevant currencies)
  const getCheckoutPrices = (usdAmount, billingCountryCode) => {
    const prices = {
      usd: { 
        amount: usdAmount, 
        formatted: `$${usdAmount.toFixed(2)}`, 
        code: 'USD' 
      }
    };

    // Add active/detected currency if different from USD
    const activeCurrency = getActiveCurrency();
    if (activeCurrency !== 'USD') {
      const localAmount = convertTo(usdAmount, activeCurrency);
      prices.local = {
        amount: localAmount,
        formatted: formatInCurrency(localAmount, activeCurrency),
        code: activeCurrency
      };
    }

    // Add billing currency if different from USD and active
    if (billingCountryCode) {
      const billingCurrency = getCurrencyForCountry(billingCountryCode);
      if (billingCurrency.code !== 'USD' && billingCurrency.code !== activeCurrency) {
        const billingAmount = convertTo(usdAmount, billingCurrency.code);
        prices.billing = {
          amount: billingAmount,
          formatted: formatInCurrency(billingAmount, billingCurrency.code),
          code: billingCurrency.code
        };
      }
    }

    return prices;
  };

  // Get all available currencies for manual selection
  const getAvailableCurrencies = () => {
    return Object.entries(ALL_CURRENCIES).map(([code, data]) => ({
      code,
      ...data
    }));
  };

  const value = {
    // State
    detectedLocation,
    selectedCurrency,
    activeCurrency: getActiveCurrency(),
    exchangeRates,
    loading,
    
    // Manual currency selection
    setCurrency,
    clearCurrencySelection,
    getAvailableCurrencies,
    
    // Conversion & Formatting
    convertTo,
    convertToActive,
    formatInCurrency,
    formatDualPrice,
    formatLocalPrice,
    
    // Gateway helpers
    gatewaySupports,
    getPaymentCurrency,
    shouldShowGateway,
    getPaymentAmount,
    getCheckoutPrices,
    
    // Legacy compatibility
    userLocation: detectedLocation,
    convertFromUSD: convertToActive,
    isNigerian: detectedLocation?.countryCode === 'NG'
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
